import React from 'react';
import classNames from 'classnames';
import { type IndividualColumn, SortingDirection } from 'src/shared/components/data-table/dataTableTypes';
import SortIcons from 'src/shared/components/sort-icons/SortIcons';
import useDataTable from 'src/shared/components/data-table/hooks/useDataTable';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import styles from '../../TableHeader.module.scss';

const TableHeaderCell = (props: IndividualColumn) => {
    const {
        title,
        isSortable,
        columnId,
        width,
        headerCellClassName,
        titleSlug,
        titleDetailedExplainerSlug,
        onTitleDetailedExplainerClick,
    } = props;

    const { dispatch, sortedColumn, allowSortClear, onSort } = useDataTable();

    const { columnId: sortedColumnId, sortedDirection } = sortedColumn || {};

    if (!dispatch) {
        return null;
    }

    // Map SortingDirection enum to SortIcons sortDirection prop
    const getSortDirection = (): 'asc' | 'desc' | null => {
        if (columnId === sortedColumnId) {
            switch (sortedDirection) {
                case SortingDirection.ASC:
                    return 'asc';
                case SortingDirection.DESC:
                    return 'desc';
                default:
                    return null;
            }
        }
        return null;
    };

    // Determine if SortIcons should be disabled
    const isSortIconsDisabled = !isSortable;

    const handleSort = () => {
        let directionToSet = SortingDirection.NONE;
        if (columnId !== sortedColumnId || sortedDirection === SortingDirection.NONE) {
            directionToSet = SortingDirection.DESC;
        } else if (sortedDirection === SortingDirection.DESC) {
            directionToSet = SortingDirection.ASC;
        } else if (sortedDirection === SortingDirection.ASC) {
            if (allowSortClear) {
                dispatch({
                    type: 'clear_sorted_column',
                });
                return;
            } else {
                directionToSet = SortingDirection.DESC;
            }
        }

        dispatch({
            type: 'set_sorted_column',
            payload: {
                columnId,
                sortedDirection: directionToSet,
            },
        });

        // Call the onSort callback for analytics tracking
        onSort?.(columnId);
    };

    const headerCellClassNames = classNames(styles.headerCell, {
        [styles.headerCellSorted]: columnId === sortedColumnId,
        headerCellClassName,
    });

    const titleClassNames = classNames(styles.title, {
        [styles.titleSortable]: isSortable,
        [styles.titleActive]: columnId === sortedColumnId,
    });

    const getAriaSort = (): React.AriaAttributes['aria-sort'] => {
        if (columnId !== sortedColumnId) return undefined;
        if (sortedDirection === SortingDirection.ASC) return 'ascending';
        if (sortedDirection === SortingDirection.DESC) return 'descending';
        return undefined;
    };

    const helpContent = titleSlug ? (
        <span
            className={styles.headerHelpAnchor}
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
            role='presentation'
        >
            <QuestionButtonHelp
                type='api'
                placement='right'
                slug={titleSlug}
                detailedExplainerSlug={titleDetailedExplainerSlug}
                onDetailedHelpClick={onTitleDetailedExplainerClick}
                zIndex={1300}
            />
        </span>
    ) : null;

    return (
        <th className={headerCellClassNames} style={{ width, minWidth: width }} aria-sort={getAriaSort()} data-testid='table-header-cell'>
            <div className={styles.headerCellContent}>
                {isSortable ? (
                    <button onClick={handleSort} className={classNames(styles.sortButton, titleClassNames)}>
                        {title}
                    </button>
                ) : (
                    <div className={titleClassNames}>{title}</div>
                )}
                {helpContent}
                {isSortable && (
                    <div
                        onClick={handleSort}
                        className={classNames(styles.sortArrowContainer, styles.sortArrow, {
                            [styles.sortArrowActive]: columnId === sortedColumnId,
                        })}
                        data-testid='sort-arrow'
                    >
                        <SortIcons sortDirection={getSortDirection()} disabled={isSortIconsDisabled} />
                    </div>
                )}
            </div>
        </th>
    );
};

export default TableHeaderCell;
